import { useEffect, useRef, useState } from "react";
import { AttributionControl, CircleMarker, LayerGroup, LayersControl, Popup, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import useTheme from "@mui/material/styles/useTheme";
import pyxisLogo from "../../assets/branding/pyxis.svg";
export const MAP_MAX_ZOOM = 20;
const MAP_MAX_NATIVE_ZOOM = 19;
const ESRI_ATTRIBUTION = "Tiles &copy; Esri";
const PYXIS_ATTRIBUTION = `<a href="https://pyxis.tech" target="_blank" rel="noopener noreferrer" aria-label="pyxis"><img class="proteo-map-attribution-logo" src="${pyxisLogo}" alt="pyxis" /></a>`;
const STREET_MAP_URL = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}";
const DARK_MAP_URL = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}";
const DARK_LABELS_URL = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}";
const SATELLITE_URL = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
export type Coordinates = [number, number];

export interface NearbyMapPlace {
  id: string;
  label: string;
  position: Coordinates;
}

export const COMMUNITY_LOCATION_THRESHOLD_METERS = 1500;

const distanceInMeters = (from: Coordinates, to: Coordinates) => {
  const earthRadius = 6371000;
  const toRadians = (value: number) => value * Math.PI / 180;
  const deltaLatitude = toRadians(to[0] - from[0]);
  const deltaLongitude = toRadians(to[1] - from[1]);
  const latitude = toRadians((from[0] + to[0]) / 2);
  const x = deltaLongitude * Math.cos(latitude);
  const y = deltaLatitude;
  return Math.sqrt(x * x + y * y) * earthRadius;
};
function DeviceLocationControl({
  nearbyPlaces,
  onNearbyPlace,
}: {
  nearbyPlaces: NearbyMapPlace[];
  onNearbyPlace?: (place: NearbyMapPlace, distanceMeters: number, userPosition: Coordinates) => void;
}) {
  const map = useMap();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [position, setPosition] = useState<Coordinates | null>(null);

  useEffect(() => {
    const control = new L.Control({ position: "bottomright" });

    control.onAdd = () => {
      const container = L.DomUtil.create("div", "leaflet-control proteo-map-location-control");
      const button = L.DomUtil.create("button", "proteo-map-location-toggle", container) as HTMLButtonElement;
      button.type = "button";
      button.setAttribute("aria-label", "Usar mi ubicación");
      button.title = "Usar mi ubicación";
      buttonRef.current = button;

      L.DomEvent.disableClickPropagation(container);
      L.DomEvent.on(button, "click", () => {
        if (!navigator.geolocation) {
          button.title = "La geolocalización no está disponible";
          return;
        }

        button.disabled = true;
        button.classList.add("is-loading");
        button.title = "Solicitando ubicación…";
        navigator.geolocation.getCurrentPosition(
          ({ coords }) => {
            const nextPosition: Coordinates = [coords.latitude, coords.longitude];
            setPosition(nextPosition);
            const nearestPlace = nearbyPlaces
              .map((place) => ({
                place,
                distanceMeters: distanceInMeters(nextPosition, place.position),
              }))
              .sort((left, right) => left.distanceMeters - right.distanceMeters)[0];
            if (nearestPlace && nearestPlace.distanceMeters <= COMMUNITY_LOCATION_THRESHOLD_METERS) {
              onNearbyPlace?.(nearestPlace.place, nearestPlace.distanceMeters, nextPosition);
            }
            map.flyTo(nextPosition, Math.max(map.getZoom(), 14), { duration: 1 });
            button.disabled = false;
            button.classList.remove("is-loading");
            button.title = "Volver a mi ubicación";
          },
          (error) => {
            button.disabled = false;
            button.classList.remove("is-loading");
            button.title = error.code === 1
              ? "Permiso de ubicación denegado"
              : "No se pudo obtener tu ubicación";
          },
          {
            enableHighAccuracy: true,
            maximumAge: 30000,
            timeout: 10000,
          }
        );
      });

      return container;
    };

    control.addTo(map);
    return () => {
      if (buttonRef.current) {
        L.DomEvent.off(buttonRef.current);
      }
      control.remove();
      buttonRef.current = null;
    };
  }, [map, nearbyPlaces, onNearbyPlace]);

  return position ? (
    <CircleMarker
      center={position}
      radius={8}
      pathOptions={{ color: "#ffffff", weight: 3, fillColor: "#18a793", fillOpacity: 0.95 }}
    >
      <Popup>Tu ubicación actual</Popup>
    </CircleMarker>
  ) : null;
}

export default function MapBaseLayers({
  nearbyPlaces = [],
  onNearbyPlace,
}: {
  nearbyPlaces?: NearbyMapPlace[];
  onNearbyPlace?: (place: NearbyMapPlace, distanceMeters: number, userPosition: Coordinates) => void;
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <>
      <AttributionControl prefix={PYXIS_ATTRIBUTION} position="bottomright" />
      <DeviceLocationControl nearbyPlaces={nearbyPlaces} onNearbyPlace={onNearbyPlace} />
      <LayersControl position="bottomright" collapsed>
      <LayersControl.BaseLayer checked name="Mapa">
        <LayerGroup>
          <TileLayer
            url={isDark ? DARK_MAP_URL : STREET_MAP_URL}
            attribution={ESRI_ATTRIBUTION}
            maxZoom={MAP_MAX_ZOOM}
            maxNativeZoom={MAP_MAX_NATIVE_ZOOM}
          />
          {isDark && (
            <TileLayer
              url={DARK_LABELS_URL}
              zIndex={2}
              maxZoom={MAP_MAX_ZOOM}
              maxNativeZoom={MAP_MAX_NATIVE_ZOOM}
            />
          )}
        </LayerGroup>
      </LayersControl.BaseLayer>
      <LayersControl.BaseLayer name="Satélite">
        <TileLayer
          url={SATELLITE_URL}
          attribution={ESRI_ATTRIBUTION}
          maxZoom={MAP_MAX_ZOOM}
          maxNativeZoom={MAP_MAX_NATIVE_ZOOM}
        />
      </LayersControl.BaseLayer>
      </LayersControl>
    </>
  );
}
